/* eslint-disable jsx-a11y/label-has-associated-control */
import { useContext, useState } from 'react';
import { BsCheck2 } from 'react-icons/bs';
import { add, format, isAfter } from 'date-fns';
import PropTypes from 'prop-types';
import { Button, ButtonDropdown, SelectGeneric } from 'ypf-pas-general-ui-components';
import { MdFilterAlt, MdOutlineRemoveCircleOutline } from 'react-icons/md';
import { es } from 'date-fns/locale';
import { IoIosRefresh } from 'react-icons/io';
import { BiXCircle } from 'react-icons/bi';

import { getDaysInterval } from '../../utils/date';
import baseStyles from '../../styles/base.module.scss';
import { approveWorkReports, rejectWorkReports } from '../../../../services/workReport';
import useToast from '../../../../hooks/useToast';
import InputCalendar from '../../../Calendar/InputCalendar/InputCalendar';
import LoaderContext from '../../../../context/LoaderProvider';
import { useWorkReportModals } from '../../../../context/WorkReportModalsContext';
import { SORT_OPTIONS, locationSort } from '../../utils/consts';
import { ubicationTypeEnum } from '../../../../utils/dataStatus';
import { checkRol } from '../../../../security';
import { inspector } from '../../../../security/constantsRols';
import useLocationProgramPoints from '../../../WorkReportModals/ProgramPointModal/useLocationProgramPoints';
import { getLocationProgramPointAdapter } from '../../../../services/programPointsService';

import styles from './TableHeader.module.scss';

/**
 * @typedef Props
 * @property {boolean} areCheckedDays
 * @property {import('../../../../adapters/weekly-report/get-weekly-report-adapter').TableDataCalendar} calendar
 * @property {boolean} extendedView
 * @property {() => Promise<import('../../../../adapters/weekly-report/get-weekly-report-adapter').TableData>} fetchAllData
 * @property {boolean} filledFilter
 * @property {import('../../hooks/useFilters').Filters} filters
 * @property {() => number[]} getIDsToSend
 * @property {(newData: import('../../../../adapters/weekly-report/get-weekly-report-adapter').TableData) => void} handleCheckAll
 * @property {() => void} handleUpdateApprovedOrRejectedCells
 * @property {Date} initialDay
 * @property {boolean} isFetchingNextPage
 * @property {boolean} isInspector
 * @property {boolean} isLoading
 * @property {boolean} isTableChecked
 * @property {import('react').Dispatch<import('react').SetStateAction<boolean>>} setShowFilters
 * @property {(newStartDay: Date) => void} updateStartDay
 * @property {() => void} handleReloadTable
 */

/** @param {Props} */
const TableHeader = ({
  areCheckedDays,
  calendar,
  changeSortType,
  extendedView,
  filledFilter,
  filters,
  // fetchAllData,
  getIDsToSend,
  // handleCheckAll,
  handleUpdateApprovedOrRejectedCells,
  initialDay,
  // isFetchingNextPage,
  isInspector,
  isLoading,
  // isTableChecked,
  resetDaysToApproveOrReject,
  setShowFilters,
  updateStartDay,
  sortType,
  handleReloadTable,
  tableData
}) => {
  const { setLoader } = useContext(LoaderContext);
  const {
    activeApproveSelectedQuantitiesModal,
    activeRejectSelectedQuantitiesModal,
    activeAcceptModal
  } = useWorkReportModals();
  const { setNewToast } = useToast();
  const dateFormat = 'EEEE dd';
  const [sortValue, setSortValue] = useState(
    sortType === locationSort ? SORT_OPTIONS[0] : SORT_OPTIONS[1]
  );

  const { approveAll, approveLocationProgramPoints } = useLocationProgramPoints({});

  const initialDate = initialDay ? new Date(initialDay) : new Date();

  const [days, setDays] = useState(
    getDaysInterval({
      start: initialDate,
      end: add(initialDate, { days: 6 })
    })
  );

  const [isApproving, setIsApproving] = useState(false);

  const agrupaPorUbicacion = sortType === 'location';

  const handleWeekChange = (newDate) => {
    if (isLoading) return;

    if (newDate instanceof Date) {
      updateStartDay(newDate);
      setDays(getDaysInterval({ start: newDate, end: add(newDate, { days: 6 }), dateFormat }));

      return;
    }

    if (newDate?.$d) {
      updateStartDay(newDate.$d);
      setDays(
        getDaysInterval({ start: newDate.$d, end: add(newDate.$d, { days: 6 }), dateFormat })
      );
    }
  };

  const hasDaysToApprove = () => {
    const idToSend = getIDsToSend();

    return idToSend && idToSend.length !== 0;
  };

  const fetchApproveWorkReport = (workReportIDs) => {
    setIsApproving(true);
    setLoader(true);
    if (isInspector) {
      approveWorkReports({ partesDiariosIds: workReportIDs }, isInspector)
        .then(() => {
          setNewToast({ type: 'SUCCESS', message: 'Se han enviado los partes' });
          handleUpdateApprovedOrRejectedCells();
        })
        .catch((error) => setNewToast({ type: 'ERROR', message: error.message }))
        .finally(() => {
          setIsApproving(false);
          setLoader(false);
        });
    } else {
      approveAll(workReportIDs).then((response) => {
        if (response) {
          handleUpdateApprovedOrRejectedCells();
        }
        setIsApproving(false);
        setLoader(false);
      });
    }
  };

  const getSelectedDailyReportsWithContext = (workReportIDsValue, tableDataArray) => {
    // Suponiendo que workReportIDs es tu array de IDs seleccionados
    const selectedReports = [];

    tableDataArray.forEach(({ group, key }) => {
      group.forEach(({ secondColumn, weeklyReport }) => {
        weeklyReport.forEach((dailyReport) => {
          if (
            typeof dailyReport === 'object' &&
            dailyReport !== null &&
            workReportIDsValue.includes(dailyReport.idParteDiario)
          ) {
            const match = key.match(/\(([^)]+)\)/);
            const numeroContrato = match ? match[1] : '';

            selectedReports.push({
              ...dailyReport,
              numeroContrato: numeroContrato ?? '',
              eventoSigla: secondColumn?.eventoSigla,
              unidadMedida: secondColumn?.unidadMedida,
              lineaSap: `${secondColumn?.numeroLinea} ${secondColumn?.lineaSap}`,
              numeroPosicion: secondColumn?.numeroPosicion,
              numeroLinea: secondColumn?.numeroLinea,
              posicionSap: `${secondColumn?.numeroPosicion} ${secondColumn?.posicionSap}`
            });
          }
        });
      });
    });

    return selectedReports;
  };

  const getOperativeData = (locations) =>
    locations.some((location) =>
      location.programPoints.some(
        (pp) => pp?.code || pp?.subCode || pp?.detail || pp?.clasification || pp?.cost
      )
    );

  const checkOperativeData = async (hasOperativeData, locations, selectedDailyReports) => {
    if (hasOperativeData) {
      const result = await approveLocationProgramPoints(locations, selectedDailyReports[0]);

      if (result) {
        if (result?.statusCode === 400) {
          setNewToast({
            type: 'ERROR',
            message: result.mensaje || result.message
          });
        } else {
          setNewToast({
            type: 'SUCCESS',
            message: result.mensaje || result.message
          });
        }

        handleUpdateApprovedOrRejectedCells();
      }
    } else {
      activeAcceptModal({
        daily: selectedDailyReports[0],
        fetchData: handleUpdateApprovedOrRejectedCells,
        line: {
          positionName: selectedDailyReports[0]?.posicionSap,
          lineName: selectedDailyReports[0]?.lineaSap,
          idParteDiario: selectedDailyReports[0]?.idParteDiario,
          estadoEnum: selectedDailyReports[0]?.estadoEnum,
          tienePuntosPrograma: selectedDailyReports[0]?.notificaPorPuntosPrograma,
          puntoPrograma: selectedDailyReports[0]?.puntoPrograma,
          unidadMedida: selectedDailyReports[0]?.unidadMedida,
          eventoSigla: selectedDailyReports[0]?.eventoSigla,
          isApproval: true
        },
        editCSA: true,
        dateSelect: selectedDailyReports[0]?.fecha?.split('T')[0]
      });
    }
  };

  const handleApprove = async () => {
    const workReportIDs = getIDsToSend(); // your function to get the selected IDs

    if (workReportIDs.length > 1) {
      fetchApproveWorkReport(workReportIDs);
    } else {
      const selectedDailyReports = getSelectedDailyReportsWithContext(workReportIDs, tableData);

      if (
        selectedDailyReports[0]?.tipoUbicacionEnum === ubicationTypeEnum.pozo &&
        !checkRol(inspector)
      ) {
        try {
          setIsApproving(true);
          setLoader(true);
          // Primera consulta para obtener los puntos de programa

          const locations = await getLocationProgramPointAdapter(selectedDailyReports[0]);

          const hasOperativeData = getOperativeData(locations);

          checkOperativeData(hasOperativeData, locations, selectedDailyReports);
        } catch (error) {
          setNewToast({
            type: 'ERROR',
            message: error?.message
          });
        } finally {
          setIsApproving(false);
          setLoader(false);
        }
      } else if (selectedDailyReports) fetchApproveWorkReport(workReportIDs);
    }
  };

  const handleReject = () => {
    const workReportIDs = getIDsToSend();

    setLoader(true);
    rejectWorkReports({ partesDiariosIds: workReportIDs })
      .then((message) => {
        setNewToast({ type: 'SUCCESS', message });
        handleUpdateApprovedOrRejectedCells();
      })
      .catch((error) => setNewToast({ type: 'ERROR', message: error.message }))
      .finally(() => {
        setLoader(false);
      });
  };

  const handleAuthorizationButtonClick = () => {
    if (isInspector) {
      const idsPartesDiario = getIDsToSend();

      activeApproveSelectedQuantitiesModal({ idsPartesDiario, agrupaPorUbicacion, handleApprove });
    } else {
      handleApprove();
    }
  };

  const handleRejectButtonClick = () => {
    const idsPartesDiario = getIDsToSend();

    activeRejectSelectedQuantitiesModal({ idsPartesDiario, agrupaPorUbicacion, handleReject });
  };

  // Disable calendar when dates filter are not setted
  const initialMaxDate = new Date();
  const initialMinDate = add(initialMaxDate, { days: 3 });
  const isDisabled = !filters.fechaDesde || !filters.fechaHasta;

  const firstColumnClassName = `${styles['first-column-container']} ${
    isDisabled ? 'calendar-disabled' : ''
  }`;

  return (
    <thead>
      <tr>
        <th aria-label="calendar" className={styles.th}>
          <div className={firstColumnClassName}>
            <Button
              className={filledFilter ? 'btn-filter-active' : 'btn-filter-inactive'}
              icon={MdFilterAlt}
              onClick={() => setShowFilters(true)}
              sizeIcon="20px"
              type="button"
              variant="white"
              widthButton="40px"
            />
            <InputCalendar
              arrayDate={isLoading ? [] : calendar.pendingDates}
              extendedView={extendedView}
              firstDayOfWeek={`${format(new Date(initialDate), 'yyyy-MM-dd')}T00:00:00`}
              handleChangeDate={handleWeekChange}
              lastDayOfWeek={`${format(
                add(new Date(initialDate), { days: 6 }),
                'yyyy-MM-dd'
              )}T00:00:00`}
              maxDate={
                filters.fechaHasta || `${format(new Date(initialMaxDate), 'yyyy-MM-dd')}T00:00:00`
              }
              minDate={
                filters.fechaDesde || `${format(new Date(initialMinDate), 'yyyy-MM-dd')}T00:00:00`
              }
              selectDay={`${format(new Date(initialDate), 'yyyy-MM-dd')}T00:00:00`}
              view="weekly"
              weeklyReportTable
            />
            <SelectGeneric
              className={styles['group-select']}
              name="sortType"
              onChange={(e) => {
                setSortValue(e);
                if (e.value === 'provider') {
                  changeSortType('provider');
                } else {
                  changeSortType('location');
                }
              }}
              optionMapper={{
                value: 'value',
                label: 'label',
                nombre: 'nombre',
                id: 'id'
              }}
              options={SORT_OPTIONS}
              value={sortValue}
              withSelect="130px"
            />
            <ButtonDropdown
              label="Opciones"
              options={[
                {
                  label: 'Borrar selección',
                  onClick: resetDaysToApproveOrReject,
                  disabled: !hasDaysToApprove(),
                  icon: <MdOutlineRemoveCircleOutline />,
                  tooltipText: 'Reiniciar selección'
                },
                isInspector && {
                  label: 'Rechazar',
                  onClick: handleRejectButtonClick,
                  disabled: isLoading || !areCheckedDays,
                  icon: <BiXCircle />,
                  tooltipText: 'Rechazar'
                },
                {
                  label: 'Recargar datos',
                  onClick: handleReloadTable,
                  icon: <IoIosRefresh />,
                  tooltipText: 'Recargar datos'
                }
              ]}
            />
            <button
              className={`${baseStyles['focus-ring']} ${styles['btn-weekly-approve']}`}
              disabled={isLoading || isApproving || !areCheckedDays}
              onClick={handleAuthorizationButtonClick}
              type="button"
            >
              {isInspector ? 'Autorizar ' : 'Aprobar'} <BsCheck2 />
            </button>
          </div>
        </th>
        {days?.map((day, index) => {
          const { fechaDesde, fechaHasta } = filters;

          const startDay = add(new Date(fechaDesde), { days: -1 });
          const endDay = add(new Date(fechaHasta), { days: 1 });

          const isInDateRange = isAfter(day, startDay) && isAfter(endDay, day);

          const isDate = day instanceof Date;

          return (
            <th className={styles.th} data-day-cell-disabled={!isInDateRange} key={day}>
              <div className={styles['table-header-day']} data-day={index + 1}>
                {isDate ? format(day, dateFormat, { locale: es }) : day}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

TableHeader.propTypes = {
  areCheckedDays: PropTypes.bool,
  calendar: PropTypes.shape({
    firstWeek: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    lastWeek: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    pendingDates: PropTypes.arrayOf(PropTypes.string)
  }),
  changeSortType: PropTypes.func.isRequired,
  filledFilter: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    contratosIds: PropTypes.arrayOf(PropTypes.number),
    partesVisualizar: PropTypes.bool,
    idsEquipos: PropTypes.arrayOf(PropTypes.number),
    idsEventos: PropTypes.arrayOf(PropTypes.number),
    idsProveedores: PropTypes.arrayOf(PropTypes.number),
    fechaDesde: PropTypes.string,
    fechaHasta: PropTypes.string
  }),
  // fetchAllData: PropTypes.func,
  getIDsToSend: PropTypes.func,
  // handleCheckAll: PropTypes.func,
  handleUpdateApprovedOrRejectedCells: PropTypes.func,
  initialDay: PropTypes.instanceOf(Date).isRequired,
  // isFetchingNextPage: PropTypes.bool,
  isInspector: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  // isTableChecked: PropTypes.bool,
  resetDaysToApproveOrReject: PropTypes.func.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  sortType: PropTypes.string.isRequired,
  updateStartDay: PropTypes.func,
  extendedView: PropTypes.bool,
  handleReloadTable: PropTypes.func,
  tableData: PropTypes.arrayOf({
    key: PropTypes.string,
    group: PropTypes.arrayOf({
      firstColum: PropTypes.shape({
        agrupadoPor: PropTypes.string,
        finaliza: PropTypes.bool,
        idSolicitud: PropTypes.number,
        isDisabled: PropTypes.bool,
        tipoAsignacion: PropTypes.number,
        titulo: PropTypes.string
      }),
      secondColumn: PropTypes.shape({
        agrupadoPor: PropTypes.string,
        cambioImputacion: PropTypes.bool,
        contieneArchivosAdjuntos: PropTypes.bool,
        editaImputacion: PropTypes.bool,
        equipo: PropTypes.string,
        eventoSigla: PropTypes.string,
        idParteLinea: PropTypes.number,
        idPartetrabajo: PropTypes.number,
        idPartetrabajoUbicacionLinea: PropTypes.number,
        idProveedor: PropTypes.number,
        idSolicitud: PropTypes.number,
        idUbicacion: PropTypes.number,
        isDisabled: PropTypes.bool,
        lineaSap: PropTypes.string,
        lineaYClic: PropTypes.string,
        nroSolicitud: PropTypes.string,
        numeroLinea: PropTypes.string,
        numeroPosicion: PropTypes.string,
        objetoImputacion: PropTypes.string,
        objetoImputacionModificado: PropTypes.bool,
        posicionSap: PropTypes.string,
        posicionYClic: PropTypes.string,
        primerAgrupador: PropTypes.string,
        showHeader: PropTypes.bool,
        solicitudMensualizada: PropTypes.bool,
        tieneUbicacionesTecnicas: PropTypes.bool,
        titulo: PropTypes.string,
        ubicacion: PropTypes.string,
        unidadMedida: PropTypes.string
      }),
      weeklyReport: PropTypes.shape({
        cantidad: PropTypes.number,
        certificaYclick: PropTypes.bool,
        colorInspeccion: PropTypes.string,
        coloresEstado: PropTypes.string,
        estadoCertificacionEnum: PropTypes.number,
        estadoEnum: PropTypes.number,
        fecha: PropTypes.string,
        habilitaCheck: PropTypes.bool,
        idParteDiario: PropTypes.number,
        idSolicitud: PropTypes.number,
        nroSolicitud: PropTypes.string,
        rechazaCantidad: PropTypes.bool,
        tipoAsignacion: PropTypes.number,
        tipoUbicacionEnum: PropTypes.number
      })
    })
  })
};

TableHeader.defaultProps = {
  areCheckedDays: false,
  calendar: {},
  // fetchAllData: () => {},
  getIDsToSend: () => {},
  // handleCheckAll: () => {},
  handleUpdateApprovedOrRejectedCells: () => {},
  // isFetchingNextPage: false,
  isLoading: false,
  filters: {},
  // isTableChecked: false,
  updateStartDay: () => {},
  extendedView: false,
  handleReloadTable: () => {},
  tableData: []
};

export default TableHeader;
